import ConstructionRounded from "@mui/icons-material/ConstructionRounded"
import { Alert } from "@mui/material"
import moment from "moment"
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { subscribeMaintenance } from "./events"
import { formatMaintenanceTime } from "./format"
import { maintenanceHttpBase } from "./http"
import { isMaintenancePollFast, MAINTENANCE_POLL_INTERVAL, nextMaintenanceBoundaryDelay } from "./maintenance.constants"
import MaintenanceScreen from "./MaintenanceScreen"
import { IDLE_MAINTENANCE, MaintenanceSnapshot } from "./types"

const MaintenanceContext = createContext<MaintenanceSnapshot>(IDLE_MAINTENANCE)

export const useMaintenance = () => useContext(MaintenanceContext)

function normalizeSnapshot(raw: unknown): MaintenanceSnapshot | null {
  if (!raw || typeof raw !== "object") return null
  const envelope = raw as MaintenanceSnapshot & { data?: unknown }
  const next = (envelope.data && typeof envelope.data === "object" ? envelope.data : envelope) as MaintenanceSnapshot
  if (next.type === "ping") return null
  if (typeof next.enabled !== "boolean") return null
  return {
    enabled: next.enabled,
    title: next.title || "We'll be back soon",
    message: next.message || "The platform is undergoing scheduled maintenance. Please try again later.",
    scheduledStartAt: next.scheduledStartAt ?? null,
    scheduledEndAt: next.scheduledEndAt ?? null,
    updatedAt: next.updatedAt,
    updatedByEmail: next.updatedByEmail
  }
}

function devPreviewStatus(): MaintenanceSnapshot | null {
  if (!import.meta.env.DEV || typeof window === "undefined") return null
  if (!new URLSearchParams(window.location.search).has("previewMaintenance")) return null
  return {
    enabled: true,
    title: "We'll be back soon",
    message: "The platform is undergoing scheduled maintenance. Please try again later.",
    scheduledStartAt: null,
    scheduledEndAt: moment().add(2, "hours").add(14, "minutes").toISOString()
  }
}

const MaintenanceProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<MaintenanceSnapshot>(IDLE_MAINTENANCE)
  const preview = useMemo(() => devPreviewStatus(), [])
  const view = preview ?? status
  const pollFast = isMaintenancePollFast(status)

  const fetchStatus = useCallback(async () => {
    const base = maintenanceHttpBase()
    if (!base) return
    try {
      const res = await fetch(`${base}/maintenance/status`, {
        cache: "no-store",
        headers: { Accept: "application/json" }
      })
      if (!res.ok) return
      const next = normalizeSnapshot(await res.json())
      if (next) setStatus(next)
    } catch {
      /* keep last known snapshot */
    }
  }, [])

  useEffect(() => {
    const unsubscribe = subscribeMaintenance(setStatus)
    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    let stopped = false
    const pull = async () => {
      if (stopped) return
      await fetchStatus()
    }

    void pull()
    const period = pollFast ? MAINTENANCE_POLL_INTERVAL.onMs : MAINTENANCE_POLL_INTERVAL.offMs
    const intervalId = window.setInterval(() => {
      void pull()
    }, period)

    const onVisible = () => {
      if (document.visibilityState === "visible") void pull()
    }
    document.addEventListener("visibilitychange", onVisible)

    return () => {
      stopped = true
      window.clearInterval(intervalId)
      document.removeEventListener("visibilitychange", onVisible)
    }
  }, [fetchStatus, pollFast])

  useEffect(() => {
    const delay = nextMaintenanceBoundaryDelay(status)
    if (delay == null) return
    const timerId = window.setTimeout(() => {
      void fetchStatus()
    }, delay)
    return () => window.clearTimeout(timerId)
  }, [fetchStatus, status.enabled, status.scheduledStartAt, status.scheduledEndAt])

  const scheduledUpcoming =
    !status.enabled && Boolean(status.scheduledStartAt) && moment(status.scheduledStartAt).isAfter(moment())

  const bannerText = useMemo(() => {
    if (!scheduledUpcoming || !status.scheduledStartAt) return ""
    const end = status.scheduledEndAt ? ` until ${formatMaintenanceTime(status.scheduledEndAt)}` : ""
    return `Scheduled maintenance starts ${formatMaintenanceTime(status.scheduledStartAt)}${end}.`
  }, [scheduledUpcoming, status.scheduledEndAt, status.scheduledStartAt])

  if (view.enabled) {
    return (
      <MaintenanceContext.Provider value={view}>
        <MaintenanceScreen status={view} />
      </MaintenanceContext.Provider>
    )
  }

  return (
    <MaintenanceContext.Provider value={status}>
      {scheduledUpcoming && (
        <Alert
          icon={<ConstructionRounded sx={{ fontSize: 18 }} />}
          severity="warning"
          sx={{
            borderRadius: 0,
            py: 0.75,
            justifyContent: "center",
            fontWeight: 500,
            bgcolor: "#FFFBEB",
            color: "#92400E",
            "& .MuiAlert-icon": { color: "#D97706" }
          }}
        >
          {bannerText}
        </Alert>
      )}
      {children}
    </MaintenanceContext.Provider>
  )
}

export default MaintenanceProvider
